// src/pages/accounting/profitabilityChartHelpers.js
// ────────────────────────────────────────────────────────────────
// Extracted chart components and data helpers for
// ProjectProfitabilityPage visualization upgrade.
//
// Provides: reusable chart components for financial structure /
// performance modes, multi-project comparison, aggregate views,
// and stage-based analysis.
// ────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';

// ════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════

// Combined stage order: AD/ID pairs merged, GFC1-3 merged
export const STAGE_ORDER = [
  'advance',
  'cd', 'sd', 'dd', 'aoc', 'td',
  'gfc',
  'completion', 'cc',
];

export const STAGE_ABBREV = {
  advance: 'ADVC',
  cd: 'CD', sd: 'SD', dd: 'DD', aoc: 'AOC', td: 'TD',
  gfc: 'GFC',
  completion: 'CMPL', cc: 'CC',
};

// Map combined stage keys to the raw AD/ID timeline keys they merge
const STAGE_RAW_KEYS = {
  advance: ['advance'],
  cd: ['cd_ad', 'cd_id'],
  sd: ['sd_ad', 'sd_id'],
  dd: ['dd_ad', 'dd_id'],
  aoc: ['aoc_ad', 'aoc_id'],
  td: ['td_ad', 'td_id'],
  gfc: ['gfc1', 'gfc2', 'gfc3'],
  completion: ['completion'],
  cc: ['cc'],
};

/**
 * For a combined stage, find the latest date among its raw keys in the timeline.
 * For CC: find the earliest date (first CC invoice), including cc__YYYY-MM sub-keys.
 * Returns the date string or null if none found.
 */
function getStageDate(timeline, combinedStage) {
  if (combinedStage === 'cc') {
    // CC uses 'cc' and/or 'cc__YYYY-MM' sub-keys — find earliest
    let earliest = null;
    Object.keys(timeline).forEach(key => {
      if (key !== 'cc' && !key.startsWith('cc__')) return;
      const entry = timeline[key];
      const date = entry?.invoiceDate || entry?.actualDate || entry?.paidDate;
      if (date && (!earliest || date < earliest)) earliest = date;
    });
    return earliest;
  }
  const rawKeys = STAGE_RAW_KEYS[combinedStage] || [combinedStage];
  let latest = null;
  rawKeys.forEach(key => {
    const entry = timeline[key];
    const date = entry?.actualDate || entry?.invoiceDate || entry?.paidDate;
    if (date && (!latest || date > latest)) latest = date;
  });
  return latest;
}

// Restrained palette for multi-project comparison lines
export const COMPARE_COLORS = [
  '#537767', '#A66257', '#9F902A', '#5B7FA6', '#8B6914',
  '#6B5B95', '#88B04B', '#92A8D1',
];

// Design tokens (mirrored from page-level brioColors/brioStyles)
const colors = {
  gold: '#9F902A',
  grey: '#666666',
  border: 'rgba(0, 0, 0, 0.1)',
  expense: '#A66257',
  income: '#537767',
  black: '#111111',
};
const styles = {
  fontFamily: 'Raleway, sans-serif',
  fontWeightRegular: 300,
  fontWeightMedium: 400,
};

// ════════════════════════════════════════════════════════════════
// MONTH UTILITY (self-contained — same logic as page-level)
// ════════════════════════════════════════════════════════════════

const MO = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function shortMonth(ymStr) {
  if (!ymStr) return ymStr;
  const m = String(ymStr).match(/^(\d{4})-(\d{1,2})/);
  return m ? `${MO[parseInt(m[2]) - 1]} ${m[1].slice(2)}` : ymStr;
}

// ════════════════════════════════════════════════════════════════
// BAR-CHART BUCKETING (weekly / monthly / quarterly / yearly)
// ════════════════════════════════════════════════════════════════

// ISO 8601 week of a Date object → { year, week }.
function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // Day-of-week with Monday=0..Sunday=6
  const dayNum = (date.getUTCDay() + 6) % 7;
  // Set date to the Thursday of this ISO week (anchor)
  date.setUTCDate(date.getUTCDate() + 3 - dayNum);
  const isoYear = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstThursdayDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 3 - firstThursdayDayNum);
  const week = 1 + Math.round((date - firstThursday) / (7 * 24 * 3600 * 1000));
  return { year: isoYear, week };
}

function weekKeyFromYW(year, week) {
  return `${year}-W${String(week).padStart(2, '0')}`;
}

function weekLabel(wkKey) {
  const m = String(wkKey).match(/^(\d{4})-W(\d{2})$/);
  if (!m) return wkKey;
  return `W${m[2]} ${m[1].slice(2)}`;
}

// All ISO weeks that have at least one day in the given YYYY-MM month.
function getWeeksOverlappingMonth(ymStr) {
  const m = String(ymStr).match(/^(\d{4})-(\d{1,2})/);
  if (!m) return [];
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const keys = new Set();
  for (let day = 1; day <= lastDay; day++) {
    const { year: wy, week } = getISOWeek(new Date(Date.UTC(year, month - 1, day)));
    keys.add(weekKeyFromYW(wy, week));
  }
  // Preserve chronological order
  return Array.from(keys).sort();
}

/**
 * Choose the bar chart's bucket size based on the selected period.
 * - Mo (single month) → weekly.
 * - FY / CY → monthly (12 buckets).
 * - Custom < 3 months → weekly; 3–11 months → monthly.
 * - Multi-year (custom ≥ 12 months) and Total → quarterly up to 4 years,
 *   yearly beyond.
 *
 * `monthCount` should be the number of months in the visible window:
 *   - selectedMonths.length for periodType ∈ {month,calendarYear,indianFY,custom}
 *   - chartData.length (project's actual span) for periodType === 'total'
 */
export function selectBarBucket(periodType, monthCount) {
  if (periodType === 'month') return 'weekly';
  if (periodType === 'calendarYear' || periodType === 'indianFY') return 'monthly';
  const n = Number(monthCount) || 0;
  if (periodType === 'custom') {
    if (n > 0 && n < 3) return 'weekly';
    if (n > 0 && n < 12) return 'monthly';
  }
  // Custom ≥ 12 months and Total (any length) — multi-year cadence
  if (n > 48) return 'yearly';
  return 'quarterly';
}

const BAR_VALUE_FIELDS = ['cost', 'directCost', 'overhead', 'dormancy', 'internalOH', 'income', 'incomeNL', 'incomeIN', 'salaryGap'];

function emptyBarRow() {
  return BAR_VALUE_FIELDS.reduce((acc, k) => { acc[k] = 0; return acc; }, {});
}

/**
 * Bucket monthly chart entries into the requested cadence.
 *
 * Input rows must have: { monthKey: 'YYYY-MM', cost, directCost, overhead,
 *                         dormancy, internalOH, income }.
 *
 * Returns rows shaped: { bucketKey, label, cost, directCost, overhead,
 *                        dormancy, internalOH, income }.
 *
 * Quarterly / yearly / fyYearly: monthly entries are summed into
 * YYYY-Q[1-4], YYYY, or FY-YYYY (Apr–Mar) buckets respectively.
 *
 * Weekly: monthly values are pro-rated evenly across the ISO weeks that
 * overlap each month. Boundary weeks that touch two months receive a share
 * from both — totals over the period are preserved within rounding.
 * Direct cost and income are also pro-rated for consistency; this trades
 * per-week date accuracy for a single FX-correct path through the page's
 * existing monthly aggregator. Only used for short (<3-month) windows.
 */
export function bucketChartData(monthlyRows, bucket) {
  if (!Array.isArray(monthlyRows)) return [];
  if (bucket === 'monthly') {
    return monthlyRows.map(d => ({
      bucketKey: d.monthKey,
      label: shortMonth(d.monthKey),
      ...BAR_VALUE_FIELDS.reduce((acc, k) => { acc[k] = Number(d[k]) || 0; return acc; }, {}),
    }));
  }
  if (bucket === 'quarterly') {
    const grouped = {};
    monthlyRows.forEach(d => {
      const m = String(d.monthKey).match(/^(\d{4})-(\d{1,2})/);
      if (!m) return;
      const q = Math.floor((parseInt(m[2], 10) - 1) / 3) + 1;
      const key = `${m[1]}-Q${q}`;
      if (!grouped[key]) grouped[key] = { ...emptyBarRow(), bucketKey: key, label: `Q${q} ${m[1].slice(2)}` };
      BAR_VALUE_FIELDS.forEach(f => { grouped[key][f] += Number(d[f]) || 0; });
    });
    return Object.values(grouped).sort((a, b) => a.bucketKey.localeCompare(b.bucketKey));
  }
  if (bucket === 'yearly') {
    const grouped = {};
    monthlyRows.forEach(d => {
      const m = String(d.monthKey).match(/^(\d{4})/);
      if (!m) return;
      const key = m[1];
      if (!grouped[key]) grouped[key] = { ...emptyBarRow(), bucketKey: key, label: key };
      BAR_VALUE_FIELDS.forEach(f => { grouped[key][f] += Number(d[f]) || 0; });
    });
    return Object.values(grouped).sort((a, b) => a.bucketKey.localeCompare(b.bucketKey));
  }
  if (bucket === 'fyYearly') {
    // Indian Financial Year: Apr–Mar. Months 4–12 belong to FY of that year;
    // months 1–3 belong to FY of the previous year.
    const grouped = {};
    monthlyRows.forEach(d => {
      const m = String(d.monthKey).match(/^(\d{4})-(\d{1,2})/);
      if (!m) return;
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const fy = month >= 4 ? year : year - 1;
      const key = `FY-${fy}`;
      const label = `FY ${String(fy).slice(2)}–${String(fy + 1).slice(2)}`;
      if (!grouped[key]) grouped[key] = { ...emptyBarRow(), bucketKey: key, label };
      BAR_VALUE_FIELDS.forEach(f => { grouped[key][f] += Number(d[f]) || 0; });
    });
    return Object.values(grouped).sort((a, b) => a.bucketKey.localeCompare(b.bucketKey));
  }
  if (bucket === 'weekly') {
    const grouped = {};
    monthlyRows.forEach(d => {
      const weeks = getWeeksOverlappingMonth(d.monthKey);
      if (weeks.length === 0) return;
      const share = 1 / weeks.length;
      weeks.forEach(wk => {
        if (!grouped[wk]) grouped[wk] = { ...emptyBarRow(), bucketKey: wk, label: weekLabel(wk) };
        BAR_VALUE_FIELDS.forEach(f => { grouped[wk][f] += (Number(d[f]) || 0) * share; });
      });
    });
    return Object.values(grouped).sort((a, b) => a.bucketKey.localeCompare(b.bucketKey));
  }
  return [];
}

/**
 * Build a portfolio-level monthly aggregate of per-period (non-cumulative)
 * cost / income breakdowns. Output shape matches per-project chartData so
 * the same bucketChartData() pipeline can consume both.
 */
export function buildBarAggregateByMonth(projectDataMap, projectIds) {
  const totals = {};
  (projectIds || []).forEach(pid => {
    const data = projectDataMap[pid];
    if (!data || !Array.isArray(data.chartData)) return;
    data.chartData.forEach(d => {
      if (!totals[d.monthKey]) totals[d.monthKey] = { monthKey: d.monthKey, ...emptyBarRow() };
      BAR_VALUE_FIELDS.forEach(f => { totals[d.monthKey][f] += Number(d[f]) || 0; });
    });
  });
  return Object.values(totals).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}

// ════════════════════════════════════════════════════════════════
// DATA HELPERS
// ════════════════════════════════════════════════════════════════

/**
 * Build a time-based aggregate of cumulative series across projects.
 * All values are assumed to already be in display currency.
 *
 * Per-project chartData includes the engine-attributed salary gap in
 * its `cost` field, so summing it produces the firm-wide Loaded Cost
 * directly — no separate gap layering is needed here.
 */
export function buildAggregateByTime(projectDataMap, projectIds, essentialProfitPct) {
  const monthTotals = {};
  projectIds.forEach(pid => {
    const data = projectDataMap[pid];
    if (!data) return;
    data.chartData.forEach(d => {
      if (!monthTotals[d.monthKey]) monthTotals[d.monthKey] = { cost: 0, directCost: 0, income: 0 };
      monthTotals[d.monthKey].cost += d.cost;
      monthTotals[d.monthKey].directCost += d.directCost;
      monthTotals[d.monthKey].income += d.income;
    });
  });

  const sorted = Object.keys(monthTotals).sort();
  let cumCost = 0, cumDirectCost = 0, cumIncome = 0;
  return sorted.map(mk => {
    const monthlyCost = monthTotals[mk].cost;
    const monthlyIncome = monthTotals[mk].income;
    cumCost += monthlyCost;
    cumDirectCost += monthTotals[mk].directCost;
    cumIncome += monthlyIncome;
    return {
      month: shortMonth(mk),
      monthKey: mk,
      cumCost,
      cumDirectCost,
      cumIncome,
      cumProfit: cumIncome - cumCost,
      cumCostWithTarget: cumCost * (1 + essentialProfitPct / 100),
      cumTargetProfit: cumCost * essentialProfitPct / 100,
      monthlyProfit: monthlyIncome - monthlyCost,
    };
  });
}

/**
 * Find the cumulative data point at or before a given monthKey.
 */
function getCumulativeAtMonth(cumulativeData, targetMonthKey) {
  let result = null;
  for (const d of cumulativeData) {
    if (d.monthKey <= targetMonthKey) result = d;
    else break;
  }
  return result;
}

/**
 * Build a stage-aggregate across projects, where each stage carries the
 * INCREMENT of cost/income that accrued between the prior reached stage and
 * this one — not the cumulative-to-date snapshot. This makes the bars
 * additive: summing all stage bars equals the portfolio total for projects
 * that reached at least one stage.
 *
 * Attribution rules per project:
 *   1. Find every stage the project actually reached. Date precedence is
 *      stageTimeline first, falling back to non-draft invoices matched by
 *      stage_key (the same fallback used by extractStageMarkers).
 *   2. Sort the reached stages by their dates so increments are always
 *      chronologically non-negative even when a project's stage_timeline
 *      dates land in a slightly different order than STAGE_ORDER (e.g.
 *      first CC invoice predating CMPL).
 *   3. First reached stage absorbs all cost/income from project start up
 *      to that stage's date. Subsequent stages get the delta since the
 *      previous reached stage. The last reached stage additionally absorbs
 *      everything from its date to the project's last data point — so a
 *      project on ongoing CC keeps contributing post-CC monthly billings
 *      into the CC bar.
 *   4. Projects that reached zero stages are skipped entirely; their
 *      income/cost can't be assigned to any stage and would otherwise leak.
 *
 * `invoicesByProject` (optional) is the per-project non-draft invoice list
 * used to backfill missing stageTimeline dates for older projects.
 */
export function buildAggregateByStage(projects, projectDataMap, essentialProfitPct, invoicesByProject = {}) {
  const stageIncrements = {}; // { stageKey: { cumCost, cumDirectCost, cumIncome, count } }

  projects.forEach(project => {
    const data = projectDataMap[project.id];
    if (!data || !data.cumulativeData.length) return;
    const timeline = project.stageTimeline || {};
    const projectInvoices = (invoicesByProject[project.id] || []).filter(
      inv => inv && inv.status && inv.status !== 'draft'
    );

    const reached = [];
    STAGE_ORDER.forEach(stage => {
      let date = getStageDate(timeline, stage);
      if (!date) date = getStageDateFromInvoices(projectInvoices, stage);
      if (!date) return;
      reached.push({ stage, monthKey: date.substring(0, 7) });
    });
    if (reached.length === 0) return;

    // Chronological order (not STAGE_ORDER) so deltas are non-negative
    reached.sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    const finalCum = data.cumulativeData[data.cumulativeData.length - 1];

    let priorCost = 0, priorDirectCost = 0, priorIncome = 0;
    reached.forEach((entry, i) => {
      const isLast = i === reached.length - 1;
      const cum = isLast
        ? finalCum
        : getCumulativeAtMonth(data.cumulativeData, entry.monthKey);
      if (!cum) return;
      const dCost       = Math.max(0, (cum.cumCost       || 0) - priorCost);
      const dDirectCost = Math.max(0, (cum.cumDirectCost || 0) - priorDirectCost);
      const dIncome     = Math.max(0, (cum.cumIncome     || 0) - priorIncome);
      if (!stageIncrements[entry.stage]) {
        stageIncrements[entry.stage] = { cumCost: 0, cumDirectCost: 0, cumIncome: 0, count: 0 };
      }
      stageIncrements[entry.stage].cumCost       += dCost;
      stageIncrements[entry.stage].cumDirectCost += dDirectCost;
      stageIncrements[entry.stage].cumIncome     += dIncome;
      stageIncrements[entry.stage].count++;
      priorCost       = cum.cumCost       || 0;
      priorDirectCost = cum.cumDirectCost || 0;
      priorIncome     = cum.cumIncome     || 0;
    });
  });

  return STAGE_ORDER.map(stage => {
    const inc = stageIncrements[stage];
    if (!inc || inc.count === 0) return null;
    return {
      stage: STAGE_ABBREV[stage],
      stageKey: stage,
      cumCost: inc.cumCost,
      cumDirectCost: inc.cumDirectCost,
      cumIncome: inc.cumIncome,
      cumProfit: inc.cumIncome - inc.cumCost,
      cumCostWithTarget: inc.cumCost * (1 + essentialProfitPct / 100),
      cumTargetProfit: inc.cumCost * essentialProfitPct / 100,
      projectCount: inc.count,
    };
  }).filter(Boolean);
}

/**
 * Extract stage transition markers for a single project.
 * Returns array of { stageKey, label, monthKey, month } sorted by stage order.
 *
 * Primary source is the project's stageTimeline (per-stage actualDate /
 * invoiceDate / paidDate filled in via the Timeline page). For projects
 * where those date fields were never populated — typically older projects
 * or ones whose invoices were raised outside the Timeline flow — falls
 * back to the supplied `invoices` list (non-draft only), matching by
 * `stageKey` against the same STAGE_RAW_KEYS map so AD/ID variants and
 * gfc1/2/3 all roll up into a single marker per combined stage.
 */
export function extractStageMarkers(project, invoices = []) {
  const timeline = project.stageTimeline || {};
  const nonDraftInvoices = (invoices || []).filter(
    inv => inv && inv.status && inv.status !== 'draft'
  );
  const markers = [];
  STAGE_ORDER.forEach(stage => {
    let date = getStageDate(timeline, stage);
    if (!date) date = getStageDateFromInvoices(nonDraftInvoices, stage);
    if (!date) return;
    const mk = date.substring(0, 7);
    markers.push({
      stageKey: stage,
      label: STAGE_ABBREV[stage],
      monthKey: mk,
      month: shortMonth(mk),
    });
  });
  return markers;
}

/**
 * For a combined stage, find an invoice-based date among non-draft invoices.
 * Mirrors getStageDate's per-stage convention: CC uses earliest, others use
 * latest. Prefers invoice_date, falls back to paid_date.
 *
 * Matches an invoice's stage_key against a raw stage key by either exact
 * equality OR the `__` prefix form used for part-stage variants (e.g.
 * `cd_ad__part__<id>` matches the `cd_ad` raw key; `cc__2024-03` matches the
 * `cc` raw key).
 */
function getStageDateFromInvoices(invoices, combinedStage) {
  const rawKeys = STAGE_RAW_KEYS[combinedStage] || [combinedStage];
  const matches = invoices.filter(inv => {
    const sk = inv.stageKey;
    if (!sk) return false;
    return rawKeys.some(rk => sk === rk || sk.startsWith(rk + '__'));
  });
  if (matches.length === 0) return null;
  const dates = matches
    .map(inv => inv.invoiceDate || inv.paidDate)
    .filter(Boolean);
  if (dates.length === 0) return null;
  return combinedStage === 'cc'
    ? dates.reduce((a, b) => (a < b ? a : b))
    : dates.reduce((a, b) => (a > b ? a : b));
}

/**
 * Compute shared Y-axis domain across multiple projects.
 * Returns [min, max] with 5% padding, or null if no data.
 * In performance mode, costs are negated so the domain spans both sides of zero.
 */
export function computeSharedDomain(projectDataMap, projectIds, chartMode) {
  let min = 0, max = 0;
  projectIds.forEach(pid => {
    const data = projectDataMap[pid];
    if (!data) return;
    data.cumulativeData.forEach(d => {
      if (chartMode === 'performance') {
        // Performance: costs negative, income positive
        min = Math.min(min, -(d.cumCostWithTarget || 0), d.cumProfit);
        max = Math.max(max, d.cumIncome, d.cumProfit);
      } else {
        min = Math.min(min, d.cumCost, d.cumIncome, d.cumProfit, d.cumCostWithTarget);
        max = Math.max(max, d.cumCost, d.cumIncome, d.cumProfit, d.cumCostWithTarget);
      }
    });
  });
  if (min === 0 && max === 0) return null;
  const pad = (max - min) * 0.05 || 1;
  return [min - pad, max + pad];
}

/**
 * Merge profit lines from multiple projects into a single dataset
 * for overlaid comparison chart.
 */
export function mergeProjectProfitLines(projects, projectDataMap) {
  const monthMap = {};
  projects.forEach(p => {
    const data = projectDataMap[p.id];
    if (!data) return;
    data.cumulativeData.forEach(d => {
      if (!monthMap[d.monthKey]) {
        monthMap[d.monthKey] = { month: d.month, monthKey: d.monthKey };
      }
      monthMap[d.monthKey][`profit_${p.id}`] = d.cumProfit;
    });
  });
  return Object.values(monthMap).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}

// ════════════════════════════════════════════════════════════════
// CHART COMPONENTS
// ════════════════════════════════════════════════════════════════

/**
 * Chart tooltip matching BRIO design language.
 * When useAbsValues is true, displays Math.abs() of each value (for performance mode).
 * When showLoadedCost is true, injects a synthesized "Loaded Cost" row (sum of
 * the non-Income payload entries) just above Income — used by the bar chart
 * so reviewers can read the debit total alongside the income bar.
 */
const ChartTooltip = ({ active, payload, label, fmt, useAbsValues, showLoadedCost }) => {
  if (!active || !payload || !payload.length) return null;
  const visible = payload.filter(e => e.value != null);
  const isIncomeEntry = (e) => typeof e.name === 'string' && e.name.startsWith('Income');
  const firstIncomeIdx = showLoadedCost ? visible.findIndex(isIncomeEntry) : -1;
  const lastIncomeIdx = showLoadedCost
    ? visible.reduce((acc, e, i) => isIncomeEntry(e) ? i : acc, -1)
    : -1;
  const signedVal = (e) => useAbsValues ? Math.abs(e.value) : e.value;
  const loadedCost = showLoadedCost
    ? visible.filter(e => !isIncomeEntry(e)).reduce((s, e) => s + signedVal(e), 0)
    : null;
  const totalIncome = showLoadedCost
    ? visible.filter(isIncomeEntry).reduce((s, e) => s + signedVal(e), 0)
    : null;
  const profit = showLoadedCost && totalIncome != null && loadedCost != null
    ? totalIncome - loadedCost
    : null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.96)',
      border: `1px solid ${colors.border}`,
      borderRadius: 4,
      padding: '8px 12px',
      fontSize: 11,
      fontFamily: styles.fontFamily,
      fontWeight: styles.fontWeightRegular,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <p style={{ fontWeight: styles.fontWeightMedium, marginBottom: 4, color: colors.black }}>
        {label}
      </p>
      {visible.map((entry, i) => (
        <React.Fragment key={i}>
          {showLoadedCost && i === firstIncomeIdx && loadedCost != null && (
            <p style={{ color: colors.expense, margin: '2px 0', fontWeight: styles.fontWeightMedium }}>
              Loaded Cost: {fmt(loadedCost)}
            </p>
          )}
          <p style={{ color: entry.color, margin: '2px 0' }}>
            {entry.name}: {fmt(signedVal(entry))}
          </p>
          {showLoadedCost && i === lastIncomeIdx && profit != null && (
            <p style={{
              color: profit >= 0 ? colors.income : colors.expense,
              margin: '2px 0',
              fontWeight: styles.fontWeightMedium,
            }}>
              {profit >= 0 ? 'Profit' : 'Loss'}: {fmt(Math.abs(profit))}
            </p>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Reusable profitability chart supporting financial structure and performance modes.
 *
 * Props:
 *   data              - array with cumulative fields
 *   chartMode         - 'financial' | 'performance'
 *   gradientId        - unique prefix for SVG gradient defs
 *   height            - chart height in px (default 240)
 *   tickFmt           - Y-axis tick formatter
 *   fmt               - tooltip value formatter
 *   essentialProfitPct - for legend label
 *   domain            - optional [min, max] for shared Y-axis domain
 *   stageMarkers      - optional array of { month, label }
 *   xDataKey          - x-axis data key (default 'month')
 *   designFee         - total contracted design fee; in financial mode renders a dashed gold ReferenceLine
 */
export function ProfitabilityChart({
  data,
  chartMode = 'financial',
  gradientId = 'prof',
  height = 240,
  tickFmt,
  fmt,
  essentialProfitPct = 20,
  domain,
  stageMarkers,
  xDataKey = 'month',
  designFee,
}) {
  if (!data || data.length < 2) {
    return (
      <div style={{ fontSize: 11, color: colors.grey, fontStyle: 'italic', padding: '20px 0' }}>
        Not enough data points for chart (need at least 2)
      </div>
    );
  }

  const yAxisProps = domain ? { domain, allowDataOverflow: true } : {};

  const axisStyle = { fontSize: 10, fontFamily: styles.fontFamily, fontWeight: styles.fontWeightRegular };
  const tickProps = { fill: colors.grey };

  const markerLines = stageMarkers
    ? stageMarkers.map((m, i) => (
        <ReferenceLine
          key={`stg-${i}`}
          x={m.month}
          stroke={colors.grey}
          strokeWidth={0.5}
          strokeOpacity={0.5}
          label={({ viewBox }) => (
            <text
              x={viewBox.x}
              y={(viewBox.y || 5) - 4}
              textAnchor="middle"
              fill={colors.grey}
              fontSize={7}
              fontFamily={styles.fontFamily}
              fontWeight={styles.fontWeightRegular}
            >
              {m.label}
            </text>
          )}
        />
      ))
    : null;

  if (chartMode === 'performance') {
    // Performance mode: costs on negative axis, income on positive, profit line as-is
    const perfData = data.map(d => ({
      ...d,
      negCost: -(d.cumCost || 0),
      negDirectCost: -(d.cumDirectCost || 0),
      negCostWithTarget: -(d.cumCostWithTarget || 0),
    }));

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={perfData} margin={{ top: 14 }}>
          <defs>
            <linearGradient id={`gradPerfExp-${gradientId}`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="5%" stopColor={colors.expense} stopOpacity={0.15} />
              <stop offset="95%" stopColor={colors.expense} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`gradPerfCost-${gradientId}`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="5%" stopColor={colors.expense} stopOpacity={0.08} />
              <stop offset="95%" stopColor={colors.expense} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`gradPerfInc-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colors.income} stopOpacity={0.2} />
              <stop offset="95%" stopColor={colors.income} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
          <XAxis dataKey={xDataKey} stroke={colors.grey} style={axisStyle} tick={tickProps} />
          <YAxis stroke={colors.grey} style={axisStyle} tick={tickProps} tickFormatter={tickFmt} {...yAxisProps} />
          <Tooltip content={<ChartTooltip fmt={fmt} useAbsValues />} />
          <ReferenceLine y={0} stroke={colors.black} strokeWidth={1} strokeOpacity={0.3} />
          {/* Costs on negative side: target (outermost), loaded cost, direct cost */}
          <Area type="monotone" dataKey="negCostWithTarget" name={`Target (${essentialProfitPct}%)`} stroke="rgba(159,144,42,0.4)" strokeWidth={1} strokeDasharray="4 2" fillOpacity={0.06} fill={colors.gold} />
          <Area type="monotone" dataKey="negCost" name="Loaded Cost" stroke={colors.expense} strokeWidth={1.5} fillOpacity={1} fill={`url(#gradPerfExp-${gradientId})`} />
          <Area type="monotone" dataKey="negDirectCost" name="Direct Cost" stroke={colors.expense} strokeWidth={1} strokeOpacity={0.5} fillOpacity={1} fill={`url(#gradPerfCost-${gradientId})`} />
          {/* Income on positive side */}
          <Area type="monotone" dataKey="cumIncome" name="Income" stroke={colors.income} strokeWidth={1.5} fillOpacity={1} fill={`url(#gradPerfInc-${gradientId})`} />
          {/* Profit line spans both sides */}
          <Line type="monotone" dataKey="cumProfit" name="Profit" stroke={colors.gold} strokeWidth={2} dot={false} />
          {/* Monthly profit/loss bar-like line */}
          <Line type="monotone" dataKey="monthlyProfit" name="Monthly P/L" stroke={colors.gold} strokeWidth={1} strokeOpacity={0.5} dot={false} strokeDasharray="2 2" />
          {markerLines}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  // Financial structure mode: stacked areas (target → loaded cost → direct cost → income superimposed)
  // Fee line: when the design fee is within 1.5× of the chart's natural Y-max we extend the domain
  // and draw a full dashed line; when it's far above (typical mid-project), we suppress the line so
  // the cost/income signal stays readable and show a compact "↑ Fee" indicator at the top edge.
  const designFeeNum = Number(designFee) || 0;
  let referenceMax = Array.isArray(domain) && domain.length === 2 ? Number(domain[1]) || 0 : 0;
  if (!referenceMax) {
    for (const d of data) {
      const candidate = Math.max(Number(d.cumCostWithTarget) || 0, Number(d.cumIncome) || 0);
      if (candidate > referenceMax) referenceMax = candidate;
    }
  }
  const feeFitsNaturally = designFeeNum > 0 && designFeeNum <= referenceMax * 1.5;
  const feeAboveRange = designFeeNum > 0 && !feeFitsNaturally;

  return (
    <div style={{ position: 'relative' }}>
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 14 }}>
        <defs>
          <linearGradient id={`gradTarget-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colors.gold} stopOpacity={0.18} />
            <stop offset="95%" stopColor={colors.gold} stopOpacity={0.06} />
          </linearGradient>
          <linearGradient id={`gradExp-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colors.expense} stopOpacity={0.2} />
            <stop offset="95%" stopColor={colors.expense} stopOpacity={0} />
          </linearGradient>
          <linearGradient id={`gradCost-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colors.expense} stopOpacity={0.12} />
            <stop offset="95%" stopColor={colors.expense} stopOpacity={0} />
          </linearGradient>
          <linearGradient id={`gradInc-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colors.income} stopOpacity={0.2} />
            <stop offset="95%" stopColor={colors.income} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
        <XAxis dataKey={xDataKey} stroke={colors.grey} style={axisStyle} tick={tickProps} />
        <YAxis stroke={colors.grey} style={axisStyle} tick={tickProps} tickFormatter={tickFmt} {...yAxisProps} />
        <Tooltip content={<ChartTooltip fmt={fmt} />} />
        {/* Render back-to-front: target band (largest), loaded cost, direct cost, income on top */}
        <Area type="monotone" dataKey="cumCostWithTarget" name={`Min. Profit Target (${essentialProfitPct}%)`} stroke="rgba(159,144,42,0.4)" strokeWidth={1} strokeDasharray="4 2" fillOpacity={1} fill={`url(#gradTarget-${gradientId})`} />
        <Area type="monotone" dataKey="cumCost" name="Loaded Cost" stroke={colors.expense} strokeWidth={2} fillOpacity={1} fill={`url(#gradExp-${gradientId})`} />
        <Area type="monotone" dataKey="cumDirectCost" name="Direct Cost" stroke={colors.expense} strokeWidth={1} strokeOpacity={0.5} fillOpacity={1} fill={`url(#gradCost-${gradientId})`} />
        <Area type="monotone" dataKey="cumIncome" name="Income" stroke={colors.income} strokeWidth={2} fillOpacity={1} fill={`url(#gradInc-${gradientId})`} />
        {feeFitsNaturally && (
          <ReferenceLine
            y={designFeeNum}
            stroke={colors.gold}
            strokeWidth={1}
            strokeDasharray="4 2"
            strokeOpacity={0.8}
            ifOverflow="extendDomain"
            label={{
              value: 'Fee',
              position: 'insideTopRight',
              fill: colors.gold,
              fontSize: 10,
              fontFamily: styles.fontFamily,
              fontWeight: styles.fontWeightRegular,
            }}
          />
        )}
        {markerLines}
      </ComposedChart>
    </ResponsiveContainer>
    {feeAboveRange && (
      <div
        style={{
          position: 'absolute',
          top: 2,
          right: 12,
          fontSize: 10,
          color: colors.gold,
          fontFamily: styles.fontFamily,
          fontWeight: styles.fontWeightRegular,
          pointerEvents: 'none',
        }}
        title={tickFmt ? `Design Fee ${tickFmt(designFeeNum)} (above chart range)` : 'Design Fee above chart range'}
      >
        ↑ Fee{tickFmt ? ` ${tickFmt(designFeeNum)}` : ''}
      </div>
    )}
    </div>
  );
}

/**
 * Overlaid profit-line comparison chart for multiple projects.
 */
export function CompareChart({
  mergedData,
  projects,
  height = 280,
  tickFmt,
  fmt,
  domain,
}) {
  if (!mergedData || mergedData.length < 2) {
    return (
      <div style={{ fontSize: 11, color: colors.grey, fontStyle: 'italic', padding: '20px 0' }}>
        Not enough data for comparison
      </div>
    );
  }

  const yAxisProps = domain ? { domain, allowDataOverflow: true } : {};

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={mergedData}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
        <XAxis
          dataKey="month"
          stroke={colors.grey}
          style={{ fontSize: 10, fontFamily: styles.fontFamily, fontWeight: styles.fontWeightRegular }}
          tick={{ fill: colors.grey }}
        />
        <YAxis
          stroke={colors.grey}
          style={{ fontSize: 10, fontFamily: styles.fontFamily, fontWeight: styles.fontWeightRegular }}
          tick={{ fill: colors.grey }}
          tickFormatter={tickFmt}
          {...yAxisProps}
        />
        <Tooltip content={<ChartTooltip fmt={fmt} />} />
        <ReferenceLine y={0} stroke={colors.grey} strokeWidth={1} strokeOpacity={0.5} />
        {projects.map((p, i) => (
          <Line
            key={p.id}
            type="monotone"
            dataKey={`profit_${p.id}`}
            name={p.projectCode || p.name || `Project ${p.id}`}
            stroke={COMPARE_COLORS[i % COMPARE_COLORS.length]}
            strokeWidth={1.5}
            dot={false}
            connectNulls={false}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * Grouped bar chart: per time bucket, a left stacked column for debits
 * and a right column for income (NL bottom, IN top). The debit stack is
 * direct cost + op. OH + dormancy + internal OH + a light-peach salary-
 * gap segment on top (engine-allocated labour cost that couldn't be tied
 * to a specific time entry). Stack IDs differ between the two columns
 * so Recharts groups them side-by-side within each bucket.
 *
 * Props:
 *   data    - bucketed rows from bucketChartData (label + value fields)
 *   height  - chart height in px
 *   tickFmt - Y-axis tick formatter
 *   fmt     - tooltip value formatter
 */
export function ProfitabilityBarChart({
  data,
  height = 240,
  tickFmt,
  fmt,
}) {
  if (!data || data.length < 1) {
    return (
      <div style={{ fontSize: 11, color: colors.grey, fontStyle: 'italic', padding: '20px 0' }}>
        No data for this period
      </div>
    );
  }

  const axisStyle = { fontSize: 10, fontFamily: styles.fontFamily, fontWeight: styles.fontWeightRegular };
  const tickProps = { fill: colors.grey };

  // Color ramp for the debit stack — all expense-toned, varying opacity so
  // the four components remain distinguishable while reading as one column.
  const directCostColor = colors.expense;
  const overheadColor = 'rgba(166, 98, 87, 0.65)';
  const dormancyColor = 'rgba(159, 144, 42, 0.55)';
  // Deep brick: same warm-red family as Direct Cost but darker, so it
  // stays in the cost theme while staying clearly distinguishable.
  const internalOHColor = '#6E3F37';

  // Income split: NL (EUR invoices) at the bottom in the deeper green,
  // IN (INR invoices) stacked on top in a lighter tone of the same hue.
  const incomeNLColor = colors.income;
  const incomeINColor = 'rgba(83, 119, 103, 0.55)';

  // Salary Gap segment: light peach derived from the BRIO orange token
  // (--brio-orange = #DB6B5D), kept in the warm-cost family but visually
  // distinct from the deeper expense reds used by the other debit stripes.
  const salaryGapColor = 'rgba(219, 107, 93, 0.35)';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 14, right: 12, bottom: 0, left: 0 }} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
        <XAxis dataKey="label" stroke={colors.grey} style={axisStyle} tick={tickProps} />
        <YAxis stroke={colors.grey} style={axisStyle} tick={tickProps} tickFormatter={tickFmt} />
        <Tooltip content={<ChartTooltip fmt={fmt} showLoadedCost />} cursor={false} />
        <Bar dataKey="directCost" name="Direct Cost" stackId="debit" fill={directCostColor} />
        <Bar dataKey="overhead" name="Op. Overhead" stackId="debit" fill={overheadColor} />
        <Bar dataKey="dormancy" name="Dormancy" stackId="debit" fill={dormancyColor} />
        <Bar dataKey="internalOH" name="Internal OH" stackId="debit" fill={internalOHColor} />
        <Bar dataKey="salaryGap" name="Salary Gap" stackId="debit" fill={salaryGapColor} />
        <Bar dataKey="incomeNL" name="Income (NL)" stackId="income" fill={incomeNLColor} />
        <Bar dataKey="incomeIN" name="Income (IN)" stackId="income" fill={incomeINColor} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * KPI summary bar for aggregate chart.
 */
export function AggregateKPIBar({ totalIncome, totalCost, totalProfit, gapToTarget, projectCount, fmt, salaryGap }) {
  const profitColor = totalProfit >= 0 ? colors.income : colors.expense;
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 12 }}>
      <div>
        <div style={{ fontSize: 9, color: colors.income, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Income</div>
        <div style={{ fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmt(totalIncome)}</div>
      </div>
      <div>
        <div style={{ fontSize: 9, color: colors.expense, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Loaded Cost</div>
        <div style={{ fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmt(totalCost)}</div>
      </div>
      {salaryGap != null && salaryGap > 0 && (
        <div title="Salary cost of contracted hours that were not logged to any project. Allocated to projects by hour share within each office-month, so this amount is part of every project's Loaded Cost.">
          <div style={{ fontSize: 9, color: colors.expense, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>↳ Salary Gap (incl.)</div>
          <div style={{ fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums', color: colors.expense, opacity: 0.7 }}>{fmt(salaryGap)}</div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 9, color: profitColor, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {totalProfit >= 0 ? 'Profit' : 'Loss'}
        </div>
        <div style={{ fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums', color: profitColor }}>
          {fmt(Math.abs(totalProfit))}
        </div>
      </div>
      {gapToTarget != null && (
        <div>
          <div style={{ fontSize: 9, color: colors.grey, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gap to Target</div>
          <div style={{
            fontSize: 14,
            fontWeight: 400,
            fontVariantNumeric: 'tabular-nums',
            color: gapToTarget >= 0 ? colors.income : colors.expense,
          }}>
            {gapToTarget >= 0 ? '+' : ''}{fmt(gapToTarget)}
          </div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 9, color: colors.grey, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Projects</div>
        <div style={{ fontSize: 14, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{projectCount}</div>
      </div>
    </div>
  );
}
